import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import SiretLookupField from './SiretLookupField';
import { createBooking } from '../lib/appointments';
import { useLang, t, getLocale } from '../context/LanguageContext';
import translations from '../i18n/translations';

export default function AppointmentForm({
  slot,
  onSuccess,
  onError,
  typeAlias = 'comptoir',
  importantNotice = null,
  durationMin = null,
}) {
  const { lang } = useLang();
  const tr = translations.rdv;
  const locale = getLocale(lang);
  const [form, setForm] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    motif: '',
    siret: '',
    cgv_accepted: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrMsg(null);
    try {
      const result = await createBooking({
        prenom: form.prenom,
        nom: form.nom,
        email: form.email,
        telephone: form.telephone,
        motif: form.motif,
        type_alias: typeAlias,
        start_at: slot.start,
        siret: form.siret || null,
        cgv_accepted: form.cgv_accepted,
        lang,
        // Phase 4.1 (2026-05-22) : duree au choix pour les types
        // allow_custom_duration (ex: personnalise).
        ...(durationMin ? { duration_min: durationMin } : {}),
      });
      onSuccess(result);
    } catch (err) {
      // Cas particulier prérequis : backend renvoie 409 + {detail: 'prerequisite_required', missing_alias}
      if (
        err?.status === 409 &&
        (err?.data?.detail === 'prerequisite_required' || err?.data?.missing_alias)
      ) {
        const missing = err?.data?.missing_alias || t(tr.form_diag_missing_fallback, lang);
        setErrMsg(
          `${t(tr.form_error_prereq_before, lang)} ${missing} ${t(tr.form_error_prereq_after, lang)}`,
        );
      } else {
        setErrMsg(err.message || t(tr.form_error_generic, lang));
      }
      onError?.(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-5"
      data-testid="appointment-form"
    >
      {importantNotice && (
        <div
          className="bg-amber-500/10 border border-amber-500/30 backdrop-blur-sm rounded-lg p-4"
          data-testid="important-notice-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              size={20}
              className="text-amber-300 flex-shrink-0 mt-0.5"
            />
            <p className="text-sm text-amber-200 leading-relaxed">
              {importantNotice}
            </p>
          </div>
        </div>
      )}
      <div>
        <h3 className="text-2xl font-bold text-white mb-2">{t(tr.form_title, lang)}</h3>
        <p className="text-sm text-slate-400">
          {t(tr.form_slot_chosen, lang)}{' '}
          <strong className="text-[#0099D6] font-semibold">
            {new Date(slot.start).toLocaleString(locale, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </strong>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label={t(tr.form_firstname, lang)}
          required
          value={form.prenom}
          onChange={(v) => setForm({ ...form, prenom: v })}
          testId="appt-prenom"
        />
        <Input
          label={t(tr.form_lastname, lang)}
          required
          value={form.nom}
          onChange={(v) => setForm({ ...form, nom: v })}
          testId="appt-nom"
        />
      </div>

      <Input
        label={t(tr.form_email, lang)}
        type="email"
        required
        value={form.email}
        onChange={(v) => setForm({ ...form, email: v })}
        testId="appt-email"
      />
      <Input
        label={t(tr.form_phone, lang)}
        type="tel"
        required
        value={form.telephone}
        onChange={(v) => setForm({ ...form, telephone: v })}
        placeholder={t(tr.form_phone_placeholder, lang)}
        testId="appt-telephone"
      />

      <SiretLookupField
        value={form.siret}
        onChange={(v) => setForm({ ...form, siret: v })}
      />

      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          {t(tr.form_motif, lang)} <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          required
          minLength={5}
          maxLength={200}
          value={form.motif}
          onChange={(e) => setForm({ ...form, motif: e.target.value })}
          placeholder={t(tr.form_motif_placeholder, lang)}
          className="w-full px-4 py-3 rounded-lg bg-[#0B0C10]/60 border border-white/10 text-white placeholder-slate-500 focus:ring-2 focus:ring-[#0099D6]/40 focus:border-[#0099D6]/60 outline-none transition-all"
          data-testid="appt-motif"
        />
      </div>

      <div className="bg-amber-500/5 border border-amber-400/20 p-4 rounded-lg text-sm">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            required
            checked={form.cgv_accepted}
            onChange={(e) => setForm({ ...form, cgv_accepted: e.target.checked })}
            className="mt-1 w-4 h-4 accent-[#0099D6]"
            data-testid="appt-cgv"
          />
          <span className="text-slate-300 leading-relaxed">
            {t(tr.form_cgv_accept, lang)}{' '}
            <Link
              to="/cgv-rdv"
              target="_blank"
              rel="noreferrer"
              className="text-[#0099D6] font-semibold hover:underline"
            >
              {t(tr.form_cgv_link, lang)}
            </Link>
            {t(tr.form_cgv_cancel, lang)}{' '}
            <strong className="text-amber-200">
              {t(tr.form_cgv_warning, lang)}
            </strong>
          </span>
        </label>
      </div>

      {errMsg && (
        <div
          className="text-red-300 bg-red-500/10 border border-red-400/30 p-3 rounded-lg text-sm font-medium"
          data-testid="appt-error"
        >
          {errMsg}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-4 px-8 rounded-lg w-full transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-[0_0_20px_rgba(0,153,214,0.25)] hover:shadow-[0_0_30px_rgba(0,153,214,0.4)]"
        data-testid="appt-submit"
      >
        {submitting ? t(tr.form_submitting, lang) : t(tr.form_submit, lang)}
      </button>
    </form>
  );
}

function Input({ label, type = 'text', required = false, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">
        {label}
        {required && <span className="text-red-400"> *</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-lg bg-[#0B0C10]/60 border border-white/10 text-white placeholder-slate-500 focus:ring-2 focus:ring-[#0099D6]/40 focus:border-[#0099D6]/60 outline-none transition-all"
        data-testid={testId}
      />
    </div>
  );
}
